import { Collapse, Descriptions, Tabs } from 'antd';
import React from 'react';
import { FormatDate } from '../../../constants';
import { Country } from '../../../types/country';
import { ApplicantUserCitizenship, ApplicantUserContactInformation, ApplicantUserIdentityDetails, ApplicantUserProfessional, Entity } from '../../../generated';
import { HealthProfessionViewContainer } from '../health-profession-view/health-profession-view-container';
import { CertificationByApplicantLegalDisclaimer } from '../certification-by-applicant-legal-disclaimer/certification-by-applicant-legal-disclaimer';
interface CollapsibleApplicantProfileProps {
  professional: ApplicantUserProfessional;
  citizenship: ApplicantUserCitizenship;
  identityDetails: ApplicantUserIdentityDetails;
  contactInformation: ApplicantUserContactInformation;
  applyingTo: Entity[];
  countryData: Country[];
  showAccountEstablishmentAttestation?: boolean;
}

interface Address {
  addressLine1?: string;
  city?: string;
  stateOrProvince?: string;
  zipOrPostalCode?: string;
  country?: string;
}

interface Institution {
  issuingInstitution?: {
    id?: string;
    entityName?: string;
    address?: Address;
  };
  otherIssuingInstitution?: Address & { name?: string };
}

export const CollapsibleApplicantProfile: React.FC<CollapsibleApplicantProfileProps> = (props) => {
  const professional = props.professional;
  const citizenship = props.citizenship;
  const identityDetails = props.identityDetails;
  const applyingTo = props.applyingTo;
  const contactInformation = props.contactInformation;
  const address = contactInformation?.address;
  const phone = contactInformation?.phone;

  const primaryPhoneType = contactInformation?.phone?.find(p => p?.phoneNumber === contactInformation.primaryPhone);
  
  const [expanded, setExpanded] = React.useState(true);

  const handleCollapseChange = (key: string | string[]) => {
    setExpanded(!key.includes('99'));
  };

  const phoneTypeDisplayMap = {
    home: 'Home',
    work: 'Work',
    cell: 'Cell',
    other: 'Other'
  };

  const mapPhoneTypeToDisplay = (type: string | null | undefined): string =>
    phoneTypeDisplayMap[type as keyof typeof phoneTypeDisplayMap] || 'Other';

  const getCountryName = (countryCode: string) => {
    if (!countryCode) {
      return "Unknown";
    }
    return props?.countryData?.find((country) => country.countryCode === countryCode)?.countryName ?? ' ';
  };

  const getInstitutionName = (edu: Institution) => {
    if (edu?.issuingInstitution?.id) {
      return edu.issuingInstitution.entityName;
    }
    if (edu?.otherIssuingInstitution?.name) {
      return 'Other - ' + edu.otherIssuingInstitution.name;
    }
    return 'Unknown Institution';
  };

  const getInstitutionAddress = (edu: {
    issuingInstitution: {
      issuingInstitution: {
        address: {
          addressLine1: string;
          city: string;
          stateOrProvince: string;
          zipOrPostalCode: string;
          country: string;
        };
      };
    };
    otherIssuingInstitution: {
      addressLine1: string;
      city: string;
      stateOrProvince: string;
      zipOrPostalCode: string;
      country: string;
    };
  }) => {
    const formatAddress = (address: { addressLine1?: string; city?: string; stateOrProvince?: string; zipOrPostalCode?: string; country?: string }) => {
      return (
        <>
          {address.addressLine1 && (
            <>
              {address.addressLine1} <br />
            </>
          )}
          {address.city && <>{address.city}, </>}
          {address.stateOrProvince && <>{address.stateOrProvince}, </>}
          {address.zipOrPostalCode && (
            <>
              {address.zipOrPostalCode}, <br />
            </>
          )}
          {address.country && <>{getCountryName(address.country)}</>}
        </>
      );
    };

    if (edu?.issuingInstitution?.issuingInstitution?.address) {
      return formatAddress(edu.issuingInstitution.issuingInstitution.address);
    }

    if (edu?.otherIssuingInstitution) {
      return formatAddress(edu.otherIssuingInstitution);
    }

    return 'N/A';
  };

  const tabPanes = [
    {
      label: 'Identity',
      key: '1',
      children: (
        <>
          <Descriptions layout="vertical" column={6} className="mb-5">
            <Descriptions.Item label="Last Name">{identityDetails?.lastName}</Descriptions.Item>
            <Descriptions.Item label="Rest of Name">{identityDetails?.restOfName}</Descriptions.Item>
            <Descriptions.Item label="Gender">{identityDetails?.gender === 'F' ? 'Female' : identityDetails?.gender === 'M' ? 'Male' : 'X'}</Descriptions.Item>
            <Descriptions.Item label="Date of Birth">{FormatDate(identityDetails?.dateOfBirth)}</Descriptions.Item>
          </Descriptions>
          <CertificationByApplicantLegalDisclaimer showAccountEstablishmentAttestation={props.showAccountEstablishmentAttestation} />
        </>
      )
    },
    {
      label: 'Contact',
      key: '2',
      children: (
        <div className="flex justify-between">
          <Descriptions layout="vertical" column={2} className="w-4/5">
            <Descriptions.Item label="Type">{mapPhoneTypeToDisplay(primaryPhoneType?.phoneType)} (Primary)</Descriptions.Item>
            <Descriptions.Item label="Phone">{contactInformation?.primaryPhone}</Descriptions.Item>
            {phone?.map((phone: any, index: number) => (
              phone.phoneNumber !== contactInformation?.primaryPhone && (
                <React.Fragment key={index}>
                  <Descriptions.Item label="Type">{mapPhoneTypeToDisplay(phone.phoneType)}</Descriptions.Item>
                  <Descriptions.Item label="Phone">{phone.phoneNumber}</Descriptions.Item>
                </React.Fragment>
              )
            ))}
          </Descriptions>
          <Descriptions layout="vertical" column={2} className="w-1/5">
            <Descriptions.Item label="Address">
              {address?.streetAddressLine1}
              {address?.streetAddressLine2 && (
                <>
                  <br />
                  {address?.streetAddressLine2}
                </>
              )}
              <br /> {address?.city ? address?.city + ',' : ''} {address?.zipOrPostalCode}
              <br /> {getCountryName(address?.country ?? '')}
              <br />
            </Descriptions.Item>
          </Descriptions>
        </div>
      )
    },
    {
      label: 'Citizenship',
      key: '3',
      children: (
        <Descriptions layout="vertical" column={1}>
          <Descriptions.Item label="Current Citizen of">
            <ul style={{ listStyleType: 'none', margin: 0, padding: 0 }}>
            {citizenship?.currentCitizenOf?.map((currentCitizen, index: number) => (
              <li key={`${getCountryName(currentCitizen ?? '')}-${index}`}>{
                getCountryName(currentCitizen ?? '')}
              </li>
            ))}
            </ul>
          </Descriptions.Item>
        </Descriptions>
      )
    },
    {
      label: 'Professional',
      key: '4',
      children: (
        <>
          <Descriptions layout="vertical" className="mb-5">
            <Descriptions.Item label="Health Profession">
              <HealthProfessionViewContainer layout="vertical" displayValueOnly={true} healthProfessionCode={professional?.healthProfession ?? ''} />
            </Descriptions.Item>
          </Descriptions>

          <Descriptions layout="vertical" className="mb-5">
            <Descriptions.Item label="Citizenship at Time Entering Educational Institution">{getCountryName(professional?.citizenshipAtTimeEnteredMedicalInstitution ?? "")}</Descriptions.Item>
          </Descriptions>

          <div className="pl-2">
            <p className='mb-0'>Primary Educational Institution Information</p>

            {professional?.primaryEducationalInformation?.map((edu: any, index: number) => (
              <div className="flex flex-col" key={index}>
                <Descriptions layout="vertical" className="w-3/4 mb-5 mt-5">
                  <Descriptions.Item label="Degree Title" className="">{edu?.degreeTitle}</Descriptions.Item>
                  <Descriptions.Item label="Attendance Start Date">{FormatDate(edu?.attendanceStartedAt)}</Descriptions.Item>
                  <Descriptions.Item label="Attendance End Date">{FormatDate(edu?.attendanceEndedAt)}</Descriptions.Item>
                </Descriptions>

                <Descriptions layout="vertical" className="w-3/4 mb-5">
                  <Descriptions.Item label="Institution Name">
                    {getInstitutionName(edu)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Date Certificate Issued">{FormatDate(edu?.degreeIssuedAt)}</Descriptions.Item>
                  <Descriptions.Item label="Graduation Year">{edu?.graduationYear}</Descriptions.Item>
                </Descriptions>

                <Descriptions layout="vertical" column={1}>
                  <Descriptions.Item label="Institution Address">{getInstitutionAddress(edu)}</Descriptions.Item>
                </Descriptions>
              </div>
            ))}
          </div>
        </>
      )
    },
    {
      label: 'Organization',
      key: '5',
      children: (
        <div>
          <p>Organizations to where I’m applying</p>
          <Descriptions layout="vertical" column={1}>
            <Descriptions.Item label="Sent To">
              <ul style={{ listStyleType: 'none', margin: 0, padding: 0 }}>
                {applyingTo?.map((entity: any, index: number) => (
                  <li key={`${entity?.entityName}-${index}`}>{entity?.entityName}</li>
                ))}
              </ul>
            </Descriptions.Item>
          </Descriptions>
        </div>
      )
    }
  ];

  const collapsePanel = [
    {
      key: '99',
      label: (
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ textAlign: 'left' }}>Email: {props.contactInformation?.emailAddress}</div>
          <div style={{ textAlign: 'center' }}>Phone: {props.contactInformation?.primaryPhone}</div>
          <div style={{ textAlign: 'right' }}>{expanded ? 'Expand' : 'Collapse'}</div>
        </div>
      ),
      children: <Tabs items={tabPanes.map((pane) => ({ ...pane, key: pane.key }))} />
    }
  ];

  return <Collapse expandIconPosition="end" onChange={handleCollapseChange} items={collapsePanel} />;
};
